/**
 * Choix d'un exercice à ajouter dans une routine (`ARCHITECTURE_V2.md` §4.5, §3.1).
 *
 * 🔴 **Aucune recherche n'est réécrite ici, aucun filtre non plus.** L'étape 4 a
 * posé la réplique (`EXERCISES_KEY`, une seule entrée de cache, persistée), le
 * filtre (`searchExercisesLocally`, classé par `shared/search.ts` — le **même**
 * module que le serveur) et les facettes (`exerciseFacets`). Ce composant ne
 * fait que les brancher, avec exactement la même barre de filtres et la même
 * vignette que la bibliothèque : c'est ce qui garantit que chercher un exercice
 * depuis une routine rend les mêmes résultats, dans le même ordre, avec les
 * mêmes filtres — et **sans réseau**.
 *
 * ⚠️ **Une `Sheet`, donc par portail** (§4.5). Elle s'ouvre depuis une liste
 * défilante, et le §6.3 de la V1 a déjà payé ce défaut une fois : `position:
 * fixed` ne se résout pas contre la fenêtre sous un ancêtre transformé.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { readError } from '../../api/client';
import { EXERCISES_KEY, fetchExercises } from '../../api/exercises';
import { Button, GlyphSearch, TextField } from '../../components/nourri';
import { Hint, Sheet } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { exerciseFacets, searchExercisesLocally } from '../../lib/localSearch';
import { ExerciseFilterBar } from './ExerciseFilterBar';
import { filtersOf, NO_EXERCISE_FILTER, type ExerciseFilterState } from './exerciseFilters';
import { ExerciseThumb } from './ExerciseThumb';

/** Plafond de **rendu**, pas de recherche — cf. `ExercisesSection` (60 lignes). */
const RENDU_MAX = 40;

export function ExercisePickerSheet({
  open,
  onPick,
  onClose,
}: {
  open: boolean;
  /**
   * ⚠️ **La vignette voyage avec le choix.** Sans elle, un exercice ajouté en
   * pleine séance resterait sans image jusqu'à la première réponse du serveur —
   * c'est-à-dire, hors ligne, jamais. Défaut trouvé à l'usage le 29/08/2026 sur
   * le plan d'une séance, et il se serait reproduit ici à l'identique.
   */
  onPick: (exercise: {
    clientId: string;
    name: string;
    mediaKey: string | null;
    imageCount: number;
  }) => void;
  onClose: () => void;
}) {
  const [term, setTerm] = useState('');
  const [filtres, setFiltres] = useState<ExerciseFilterState>(NO_EXERCISE_FILTER);

  /*
   * ⚠️ La **même** entrée de cache que la bibliothèque : ouvrir cette feuille
   * ne déclenche aucun appel supplémentaire si l'onglet Exercices a déjà été
   * consulté, et hors ligne elle lit la réplique restaurée par `lib/persist.ts`.
   */
  const exercises = useQuery({ queryKey: EXERCISES_KEY, queryFn: fetchExercises });
  const liste = useMemo(() => exercises.data ?? [], [exercises.data]);
  const facettes = useMemo(() => exerciseFacets(liste), [liste]);
  const resultats = useMemo(
    () => searchExercisesLocally(liste, filtersOf(filtres, term)),
    [liste, filtres, term],
  );

  return (
    <Sheet open={open} title="Ajouter un exercice" onClose={onClose}>
      <TextField
        shape="pill"
        lead={<GlyphSearch />}
        aria-label="Chercher un exercice, un muscle ou un matériel"
        placeholder="Chercher un exercice, un muscle…"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        selectOnFocus={false}
      />

      <ExerciseFilterBar facets={facettes} value={filtres} onChange={setFiltres} />

      {exercises.isPending && <Hint>Chargement…</Hint>}
      <ReadNote error={readError(exercises)} quoi="la base d’exercices" />

      {!exercises.isPending && liste.length === 0 && readError(exercises) === null && (
        <p className="n-empty">
          Aucun exercice en base. Créez-en un depuis Muscu → Exercices.
        </p>
      )}

      {liste.length > 0 && resultats.length === 0 && (
        <p className="n-empty">Aucun exercice ne correspond.</p>
      )}

      {resultats.length > 0 && (
        <ul className="n-list">
          {resultats.slice(0, RENDU_MAX).map((row) => (
            <li key={row.clientId} className="n-list__row">
              {/*
                ⚠️ La vignette est **hors** du bouton, comme dans la liste de la
                bibliothèque : elle est `aria-hidden`, et l'inclure dans la
                cible ferait porter au bouton un contenu que le lecteur d'écran
                ignore — donc un nom accessible calculé sur une image vide.
              */}
              <ExerciseThumb mediaKey={row.mediaKey} imageCount={row.imageCount} />
              <button
                type="button"
                className="n-list__main"
                onClick={() => {
                  onPick({
                    clientId: row.clientId,
                    name: row.name,
                    mediaKey: row.mediaKey,
                    imageCount: row.imageCount,
                  });
                }}
              >
                <span className="n-list__name">{row.name}</span>
                <span className="n-list__meta">
                  {[row.primaryMuscles.join(', '), row.equipment]
                    .filter((item): item is string => item != null && item !== '')
                    .join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {resultats.length > RENDU_MAX && (
        <p className="n-hint" style={{ margin: 0 }}>
          {String(resultats.length - RENDU_MAX)} autres — affinez la recherche.
        </p>
      )}

      <Button variant="secondary" shape="rounded" block onClick={onClose}>
        Annuler
      </Button>
    </Sheet>
  );
}
